/**
 * What the pricing windows sell and the words they show for it. Prices come
 * from the pricebook, so a price change reaches the tiles, the yearly saving
 * and the recurring-charge authorization at once. Pure, for the client bundle.
 */

import { BILLING_PLAN_NAMES, type BillingCycle, type BillingPlanKey } from "./plans";
import {
  BILLING_PRICE_CENTS,
  billingIntroOffer,
  formatUsdCents,
  introOfferDiscountCents,
  yearlySavingCents,
} from "./planPricebook";

/**
 * Plans a pricing window offers at checkout, fewest features first.
 * Enterprise is priced in the pricebook but stays "Coming soon" here.
 */
export const CHECKOUT_PLAN_KEYS = ["vault", "executive"] as const satisfies readonly BillingPlanKey[];

export type CheckoutPlanKey = (typeof CHECKOUT_PLAN_KEYS)[number];

export function isCheckoutPlan(plan: string): plan is CheckoutPlanKey {
  return (CHECKOUT_PLAN_KEYS as readonly string[]).includes(plan);
}

/** `cadence` is a message key ("month" or "year"). */
export type CheckoutPlanPrice = { amount: string; cadence: "month" | "year" };

export function checkoutPlanPrice(plan: CheckoutPlanKey, cycle: BillingCycle): CheckoutPlanPrice {
  return {
    amount: formatUsdCents(BILLING_PRICE_CENTS[plan][cycle]),
    cadence: cycle === "monthly" ? "month" : "year",
  };
}

/** "$40": what a year of `plan` saves against twelve monthly payments. */
export function checkoutPlanYearlySaving(plan: CheckoutPlanKey): string {
  return formatUsdCents(yearlySavingCents(plan));
}

/**
 * The intro price a plan and cycle sell at for a new subscriber: "$45" a
 * month for `months` months, then `regularAmount`; `percentOff` is the
 * discount, rounded down so a claim never overstates it ($80 to $45 is 43).
 * Null when the plan has no offer on that cycle or the account is not
 * eligible (see `isEligibleForIntroOffer`).
 */
export type CheckoutPlanIntro = { amount: string; months: number; regularAmount: string; percentOff: number };

export function checkoutPlanIntro(
  plan: CheckoutPlanKey,
  cycle: BillingCycle,
  introEligible: boolean,
): CheckoutPlanIntro | null {
  const offer = introEligible ? billingIntroOffer(plan, cycle) : null;
  if (!offer) return null;
  return {
    amount: formatUsdCents(offer.introCents),
    months: offer.months,
    regularAmount: formatUsdCents(BILLING_PRICE_CENTS[plan][cycle]),
    percentOff: Math.floor((introOfferDiscountCents(offer) * 100) / BILLING_PRICE_CENTS[plan][cycle]),
  };
}

function planChargeTerms(plan: CheckoutPlanKey, introEligible: boolean): string {
  const { monthly, yearly } = BILLING_PRICE_CENTS[plan];
  const intro = checkoutPlanIntro(plan, "monthly", introEligible);
  const monthlyTerms = intro
    ? `${intro.amount}/month for the first ${intro.months} months, then ${formatUsdCents(monthly)}/month,`
    : `${formatUsdCents(monthly)}/month`;
  return `${BILLING_PLAN_NAMES[plan]} at ${monthlyTerms} or ${formatUsdCents(yearly)}/year`;
}

/**
 * The opening of the recurring-charge consent, naming every plan the checkbox
 * covers with both of its prices: "I authorize recurring charges for Vault at
 * $20/month or $200/year." For more than one plan it reads "for the plan I
 * choose: Vault at …, or Professional at …". An account eligible for an intro
 * offer sees its terms too: "Professional at $45/month for the first 3
 * months, then $80/month, or $800/year". English only, like the rest of the
 * consent text.
 */
export function recurringChargeAuthorization(
  plans: readonly CheckoutPlanKey[],
  { introEligible }: { introEligible: boolean },
): string {
  const terms = plans.map((plan) => planChargeTerms(plan, introEligible));
  if (terms.length === 0) throw new Error("A recurring-charge authorization needs at least one plan.");
  if (terms.length === 1) return `I authorize recurring charges for ${terms[0]}.`;
  const last = terms[terms.length - 1];
  return `I authorize recurring charges for the plan I choose: ${terms.slice(0, -1).join(", ")}, or ${last}.`;
}

/**
 * "16%": the smallest yearly saving, in whole percent, across the plans on
 * sale, so one "Save" badge on the yearly switch is true for every plan.
 */
export function checkoutYearlySavingPercent(): string {
  const percents = CHECKOUT_PLAN_KEYS.map((plan) =>
    Math.floor((yearlySavingCents(plan) * 100) / (BILLING_PRICE_CENTS[plan].monthly * 12)));
  return `${Math.min(...percents)}%`;
}
